import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Cpu, Brain, Network, ArrowUpRight, CloudLightning, Linkedin } from 'lucide-react';
import { USER_INFO } from '../data';

export const HomeView = ({ setTab, onOpenDossier }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="space-y-16"
      data-testid="home-view"
    >
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
        <div className="lg:col-span-7 space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
            <span className="w-2.5 h-2.5 rounded-full bg-[#39ff14] signal-dot shrink-0" />
            <span className="font-mono text-[10px] text-[#39ff14] tracking-widest font-semibold uppercase">
              SYSTEM CHANNELS ACTIVE
            </span>
          </div>

          <div className="space-y-2">
            <h1 className="font-display font-light text-slate-400 text-xs tracking-widest uppercase">
              BLOCKCHAIN · AI · CLOUD &mdash; DIGITAL TRANSFORMATION LEADER
            </h1>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-white tracking-tight leading-[1.05] uppercase" data-testid="hero-name">
              {USER_INFO.name}
            </h2>
            <div className="h-1 w-20 bg-gradient-to-r from-[#00f0ff] to-[#9d4edd] rounded" />
          </div>

          <p className="text-slate-300 font-light text-sm sm:text-base leading-relaxed max-w-xl">
            {USER_INFO.brief} Working directly with the Wholesale Banking CIO as{' '}
            <span className="text-[#00f0ff] font-medium font-mono text-xs">{USER_INFO.title}</span>.
          </p>

          <div className="flex flex-wrap gap-4 pt-2">
            <button
              onClick={() => setTab('achievements')}
              className="px-6 py-3 rounded font-mono font-bold text-xs tracking-wider bg-slate-900 border border-slate-800 hover:border-[#00f0ff]/50 hover:text-[#00f0ff] transition-all flex items-center gap-2 group cursor-pointer"
              data-testid="cta-explore-portfolio"
            >
              <span>EXPLORE PLATFORMS</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-[#00f0ff] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
            <a
              href={USER_INFO.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded font-display font-bold text-xs tracking-widest bg-gradient-to-r from-[#0a66c2] to-[#0073e6] hover:from-[#0073e6] hover:to-[#0a66c2] text-white transition-all shadow-lg shadow-[#0a66c2]/30 hover:scale-[1.02] cursor-pointer flex items-center gap-2 group"
              data-testid="cta-linkedin"
            >
              <Linkedin className="w-3.5 h-3.5" />
              <span>CONNECT ON LINKEDIN</span>
              <ArrowUpRight className="w-3 h-3 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <button
              onClick={onOpenDossier}
              className="px-6 py-3 rounded font-mono font-bold text-xs tracking-wider bg-slate-900 border border-slate-800 hover:border-[#00f0ff]/50 hover:text-[#00f0ff] transition-all flex items-center gap-2 group cursor-pointer"
              data-testid="cta-download-cv"
            >
              <span>DOWNLOAD DOSSIER</span>
              <ArrowUpRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-[#00f0ff] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </div>

        <div className="lg:col-span-5 relative flex justify-center">
          <div className="relative w-full max-w-[340px] aspect-[4/5] rounded-2xl overflow-hidden glass-card p-2 border border-slate-800/80 group">
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent z-10 pointer-events-none" />
            <img
              src={USER_INFO.portraitUrl}
              alt="Rishab Salgotra portrait"
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover rounded-xl grayscale opacity-80 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700"
            />
            <div className="absolute bottom-5 left-5 right-5 z-20 space-y-2">
              <div className="glass-card px-3.5 py-2.5 rounded-lg border border-[#00f0ff]/30 bg-slate-950/90 backdrop-blur-md shadow-lg flex items-center justify-between">
                <div>
                  <div className="font-mono text-[8px] text-[#00f0ff] uppercase tracking-widest">COMPUTE EFFICIENCY</div>
                  <div className="font-display font-extrabold text-white text-base tracking-tight leading-none mt-0.5">99.82%</div>
                </div>
                <Cpu className="w-5 h-5 text-[#00f0ff] signal-dot" />
              </div>
            </div>
            <div className="absolute top-5 left-5 z-20 font-mono text-[9px] text-slate-400 bg-slate-950/70 py-1 px-2.5 rounded border border-slate-800 backdrop-blur">
              HUB: DXB_NODE_01
            </div>
            <div className="absolute top-5 right-5 z-20 font-mono text-[9px] text-[#39ff14] bg-slate-950/70 py-1 px-2.5 rounded border border-[#39ff14]/30 backdrop-blur">
              LATENCY: 1.2ms
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h3 className="font-mono text-[10px] text-[#00f0ff] uppercase tracking-widest font-semibold">01 // CORE MATRICES</h3>
          <h4 className="font-display font-black text-2xl text-white tracking-tight uppercase">SYSTEM CAPABILITY NODES</h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Brain, color: '#00f0ff', glow: 'neon-glow-blue', title: 'AI Modernization', desc: 'Formulates compliant, private fine-tuning (RLHF) and ingestion pipelines for highly-regulated banking assistants and cognitive transaction layers.' },
            { icon: Network, color: '#9d4edd', glow: 'neon-glow-purple', title: 'Ledger Engineering', desc: 'Solves transaction ledger finality constraints utilizing Zero-Knowledge proofs, custom tokenizers, and high-frequency settlement channels.' },
            { icon: CloudLightning, color: '#39ff14', glow: 'neon-glow-green', title: 'Cloud Platform scaling', desc: 'Aligns cloud microservices, API Connected Banking meshes, and strict compliance security baselines across multi-cloud environments.' },
          ].map((node) => {
            const Icon = node.icon;
            return (
              <div key={node.title} className={`p-6 glass-card rounded-xl border border-slate-850 hover:${node.glow} transition-all duration-300 space-y-4`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border" style={{ background: `${node.color}1a`, color: node.color, borderColor: `${node.color}33` }}>
                  <Icon className="w-5 h-5" />
                </div>
                <h5 className="font-display font-bold text-sm text-white tracking-wide uppercase">{node.title}</h5>
                <p className="text-xs text-slate-400 leading-relaxed">{node.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="glass-card rounded-xl border border-slate-800/80 overflow-hidden relative shadow-2xl p-6 md:p-8">
        <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-bl from-[#00f0ff]/5 via-[#9d4edd]/5 to-transparent blur-3xl pointer-events-none" />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative">
          <div className="lg:col-span-6 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#9d4edd]/10 border border-[#9d4edd]/25 rounded text-[9px] font-mono font-semibold text-[#9d4edd] uppercase">
              FEATURED ARTIFACT
            </div>
            <h4 className="font-display font-black text-2xl text-white uppercase tracking-tight">
              NextGen Ledger: Private Settlement State
            </h4>
            <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed">
              Fabricates an abstract cross-ledger hub reconciling classical relational payment streams with secure digital assets. Maximizes transaction speeds while keeping absolute zero-knowledge metadata compliance on-chain.
            </p>
            <div className="grid grid-cols-3 gap-4 border-t border-slate-900/80 pt-4">
              <div><div className="font-mono text-[9px] text-[#00f0ff] uppercase">Latency</div><div className="font-display font-extrabold text-[#f1f5f9] text-sm mt-0.5">1.2 seconds</div></div>
              <div><div className="font-mono text-[9px] text-slate-500 uppercase">Scale</div><div className="font-display font-extrabold text-emerald-400 text-sm mt-0.5">3.2x Efficiency</div></div>
              <div><div className="font-mono text-[9px] text-slate-500 uppercase">Audit</div><div className="font-display font-extrabold text-[#9d4edd] text-sm mt-0.5">Pass (Sec-Aud)</div></div>
            </div>
            <div className="pt-4">
              <button
                onClick={() => setTab('achievements')}
                className="px-4 py-2 bg-[#00f0ff] text-slate-950 font-mono font-bold text-[10px] tracking-wider uppercase rounded hover:bg-[#00d0ee] transition-all cursor-pointer"
                data-testid="btn-featured-view"
              >
                VIEW FULL BLUEPRINTS
              </button>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="aspect-[16/10] rounded-xl overflow-hidden border border-slate-900 shadow-inner group relative">
              <img
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuDaTyByiV7T2Hi1SvUuS7HLJRuCI5cuPL6ziZSfCumWB45HqJYRUywqc0_jp-wEF4V8-SpkmsrRMHsXgPsSYNIjA0d11EGyqgP-uK2JuazsHrQbGE0BiysHsR1_BXLSwuGFLX4NgvJKZaKBPB85elYmQJ-kvY87wyv9n2OhpA_kKq59HsMVJN1YGioyVacIwTTEPcMF7dokUY7-vZ5kblBAAoECVZa0By857dzX2ieI4eG2_kt_ZncHy5qUzi6Hs3kihmFrjbTcJ5Q"
                alt="NextGen Ledger Blueprint"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-x-0 bottom-0 bg-slate-950/80 backdrop-blur-sm p-3 border-t border-slate-900 font-mono text-[8px] text-slate-400 flex items-center justify-between">
                <span>LEDGER STATE MODEL: CONFIDENTIAL_CLEARING_SHARD</span>
                <span className="text-[#39ff14]">ACTIVE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h3 className="font-mono text-[10px] text-[#9d4edd] uppercase tracking-widest font-semibold">02 // CAREER TIMELINE</h3>
          <h4 className="font-display font-black text-2xl text-white tracking-tight uppercase">EXPERIENCE INDEX</h4>
        </div>
        <div className="space-y-4">
          <div className="p-5 glass-card rounded-xl border border-slate-850 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2.5"><ShieldCheck className="w-4 h-4 text-[#00f0ff]" /><h5 className="font-display font-bold text-sm text-white uppercase">{USER_INFO.organization}</h5></div>
              <p className="text-xs text-slate-400 font-mono">Principal Lead, Platforms & NextGen Products — Oct 2025 - Present | Dubai, UAE</p>
            </div>
            <span className="font-mono text-[10px] py-1 px-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-[#39ff14]">ACTIVE SESSION</span>
          </div>
          <div className="p-5 glass-card rounded-xl border border-slate-850 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2.5"><Cpu className="w-4 h-4 text-[#9d4edd]" /><h5 className="font-display font-bold text-sm text-slate-300 uppercase">{USER_INFO.organization}</h5></div>
              <p className="text-xs text-slate-400 font-mono">Service Owner, CIB GCN Platforms — Mar 2022 - Aug 2025 | Dubai, UAE</p>
            </div>
            <span className="font-mono text-[10px] py-1 px-2.5 bg-slate-900 border border-slate-800 rounded text-slate-400">SESSION EXPIRED</span>
          </div>
          <div className="p-5 glass-card rounded-xl border border-slate-850 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2.5"><Network className="w-4 h-4 text-slate-400" /><h5 className="font-display font-bold text-sm text-slate-300 uppercase">HSBC BANKING GROUP</h5></div>
              <p className="text-xs text-slate-400 font-mono">Delivery Head, Head of Browser Foundation Engineering — March 2020 - Feb 2022 | Pune, India</p>
            </div>
            <span className="font-mono text-[10px] py-1 px-2.5 bg-slate-900 border border-slate-800 rounded text-slate-400">17 YEARS · HSBC GROUP</span>
          </div>
        </div>
      </section>
    </motion.div>
  );
};
